// iOS-only StoreKit paywall for VYV Premium (Monthly).
//
// Apple 3.1.1: this is the COMPLIANT in-app purchase surface. It renders ONLY
// when isNativeIapEnabled() (iOS native + flag). It NEVER shows Asaas / PIX /
// card / aivyv.com purchase affordances — only StoreKit via RevenueCat.
//
// Apple 3.1.2: the subscription disclosure (duration, auto-renew, cancel in
// Settings) + Terms/Privacy links are mandatory and live here.
//
// Prices come from StoreKit (pkg.product.priceString) — never hardcoded, as
// Apple requires the store-localized price.
//
// Unlock is eventually-consistent: a successful purchase closes the sheet and
// lets the RevenueCat webhook → subscriptions → PremiumContext realtime flip
// access (1–2s). We do NOT block waiting on isPremium.

import { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, X, Crown } from "lucide-react";
import { toast } from "sonner";
import type { PurchasesOffering, PurchasesPackage } from "@revenuecat/purchases-capacitor";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/i18n/LanguageProvider";
import { isNativeIapEnabled } from "@/lib/platform";
import {
  getPremiumOfferings,
  purchasePremium,
  restorePremium,
} from "@/lib/revenuecat";

const TERMS_URL = "https://aivyv.com/terms";
const PRIVACY_URL = "https://aivyv.com/privacy";

type LoadState = "loading" | "ready" | "error";

async function openExternal(url: string): Promise<void> {
  // Native iOS: open in the in-app browser (never full-page nav, which would
  // unmount the app). @capacitor/browser is already a project dependency.
  try {
    const { Browser } = await import("@capacitor/browser");
    await Browser.open({ url });
  } catch (err) {
    console.error("[IosPaywall] failed to open external url:", err);
  }
}

interface IosPaywallProps {
  open: boolean;
  onClose: () => void;
}

export default function IosPaywall({ open, onClose }: IosPaywallProps) {
  const { t, tf } = useLanguage();
  const [state, setState] = useState<LoadState>("loading");
  const [offering, setOffering] = useState<PurchasesOffering | null>(null);
  const [processing, setProcessing] = useState(false);

  const load = useCallback(async () => {
    setState("loading");
    const off = await getPremiumOfferings();
    if (!off || !off.monthly) {
      setState("error");
      return;
    }
    setOffering(off);
    setState("ready");
  }, []);

  useEffect(() => {
    if (open && isNativeIapEnabled()) load();
  }, [open, load]);

  // Defense in depth: never render off the IAP path even if a caller slips.
  if (!isNativeIapEnabled()) return null;

  const monthly = offering?.monthly ?? null;
  const selectedPkg: PurchasesPackage | null = monthly;

  const handleBuy = async () => {
    if (!selectedPkg || processing) return;
    setProcessing(true);
    const res = await purchasePremium(selectedPkg);
    setProcessing(false);
    if (res.success) {
      toast.success(t("pricing.iap.purchaseSuccess"));
      onClose();
    } else if (res.cancelled) {
      // User backed out — silent, no toast.
    } else {
      toast.error(t("pricing.iap.purchaseError"));
    }
  };

  const handleRestore = async () => {
    if (processing) return;
    setProcessing(true);
    const { restored } = await restorePremium();
    setProcessing(false);
    if (restored) {
      toast.success(t("pricing.iap.restored"));
      onClose();
    } else {
      toast(t("pricing.iap.restoreNone"));
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.22 }}
          className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center"
          style={{ background: "rgba(2, 6, 14, 0.82)", backdropFilter: "blur(12px)" }}
          onClick={onClose}
        >
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby="ios-paywall-title"
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 60, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full sm:w-auto sm:min-w-[400px] sm:max-w-md rounded-t-3xl sm:rounded-3xl ring-1 ring-cyan-500/20 overflow-hidden max-h-[85dvh] overflow-y-auto"
            style={{
              background:
                "linear-gradient(180deg, #050811 0%, #0a0e1a 60%, #050811 100%)",
              boxShadow: "0 -10px 60px rgba(0,0,0,0.55)",
            }}
          >
            <button
              type="button"
              onClick={onClose}
              aria-label={t("paywall.moduleLocked.cta.close")}
              className="absolute top-3 right-3 z-20 w-9 h-9 flex items-center justify-center rounded-full bg-white/[0.06] hover:bg-white/[0.10] transition-colors"
            >
              <X size={16} className="text-foreground/70" />
            </button>

            <div
              className="px-6 pt-9 flex flex-col items-center text-center"
              style={{ paddingBottom: "max(1.5rem, env(safe-area-inset-bottom, 1.5rem))" }}
            >
              <div
                className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4"
                style={{
                  background:
                    "radial-gradient(circle at center, hsl(187 72% 50% / 0.22) 0%, hsl(187 72% 30% / 0.10) 70%)",
                  border: "1px solid hsl(187 72% 50% / 0.30)",
                }}
              >
                <Crown size={22} className="text-primary" />
              </div>

              {/* No pitch repeat: the Upgrade screen sells the benefits; this
                  sheet states the monthly commitment and the actual store price. */}
              <h2
                id="ios-paywall-title"
                className="font-display text-[22px] font-semibold tracking-tight text-foreground leading-snug"
              >
                {t("pricing.iap.chooseTitle")}
              </h2>

              {/* Plan options / states */}
              <div className="w-full mt-6 min-h-[120px] flex flex-col justify-center gap-2.5">
                {state === "loading" && (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-primary" />
                  </div>
                )}
                {state === "error" && (
                  <div className="flex flex-col items-center gap-3 py-4">
                    <p className="font-body text-[13px] text-muted-foreground">
                      {t("pricing.iap.loadError")}
                    </p>
                    <Button variant="outline" size="lg" onClick={load}>
                      {t("common.retry")}
                    </Button>
                  </div>
                )}
                {state === "ready" && (
                  <>
                    <div className="w-full rounded-2xl border border-primary/40 bg-primary/10 px-4 py-4 text-left">
                      <p className="font-display text-sm font-semibold">{t("pricing.monthly")}</p>
                      <p className="mt-1 text-xl font-semibold">{monthly?.product.priceString}</p>
                    </div>
                  </>
                )}
              </div>

              {state === "ready" && (
                <div className="w-full flex flex-col gap-2 mt-5">
                  <Button
                    variant="hero"
                    size="xl"
                    className="w-full"
                    disabled={processing || !selectedPkg}
                    onClick={handleBuy}
                  >
                    {processing
                      ? t("pricing.iap.processing")
                      : tf("pricing.iap.continueWith", {
                          plan: t("pricing.monthly"),
                        })}
                  </Button>

                </div>
              )}

                  <button
                    type="button"
                    onClick={handleRestore}
                    disabled={processing}
                    className="font-display text-[12px] text-foreground/55 underline underline-offset-2 hover:text-foreground/80 transition-colors py-2 disabled:opacity-50"
                  >
                    {t("pricing.restore")}
                  </button>

              {/* Apple 3.1.2 — subscription disclosure + legal links.
                  Restated directly under the CTA (not only at the very bottom)
                  so the binding terms — plan title, duration and store price —
                  stay visible even on short screens where the footer would
                  otherwise scroll off. */}
              {state === "ready" && selectedPkg && (
                <p className="font-body text-[12px] text-foreground/70 leading-relaxed mt-4">
                  {(t("pricing.monthly"))}
                  {" — "}
                  {selectedPkg.product.priceString}
                </p>
              )}
              <p className="font-body text-[11px] text-foreground/45 leading-relaxed mt-2">
                {t("pricing.iap.autoRenew")}
              </p>
              <div className="flex items-center justify-center gap-4 mt-3">
                <button
                  type="button"
                  onClick={() => openExternal(TERMS_URL)}
                  className="font-display text-[11px] text-foreground/55 underline underline-offset-2 hover:text-foreground/85 transition-colors"
                >
                  {t("pricing.iap.terms")}
                </button>
                <button
                  type="button"
                  onClick={() => openExternal(PRIVACY_URL)}
                  className="font-display text-[11px] text-foreground/55 underline underline-offset-2 hover:text-foreground/85 transition-colors"
                >
                  {t("pricing.iap.privacy")}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

